import { useNavigate } from "@tanstack/react-router";
import { Utensils } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Image } from "@/src/lib/components/custom/Image";
import { Badge } from "@/src/lib/components/ui/badge";
import { useApi } from "@/src/lib/hooks/api/use-api";
import { Card } from "../../../lib/components/ui/card";
import ItemDialog from "./ItemDialog";

export default function HeroBanner() {
	const [dialogOpen, setDialogOpen] = useState(false);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { auth, admins, menu } = useApi();
	const { data: session } = auth.useSession();
	const { data: adminCheck } = admins.checkCurrentUserIsAdmin();
	const { data: kiddyBoxData } = menu.getItemByDisplayId("K1");
	const { data: allSizes } = menu.getSizes();

	// Extract item from API response with fallback to static data
	const item = kiddyBoxData?.item;
	const itemName = item?.name || t("home.hero.kiddy_box.fallback_name");
	const itemDescription =
		item?.description || t("home.hero.kiddy_box.fallback_description");
	const itemImage = item?.imageUrl || "/static/categories/Kiddy-Box/icon.webp";

	// Filter sizes for the kiddy box item
	const itemSizes = item
		? allSizes?.filter(
				(size) => size.menuItemId === item.id && size.isAvailable,
			) || []
		: [];

	const handleCtaClick = () => {
		// If not logged in, redirect to signin
		if (!session?.user) {
			navigate({ to: "/auth/signin" });
			return;
		}

		// If admin, do nothing (or you could show a message)
		if (adminCheck?.isAdmin) {
			return;
		}

		// If logged in and not admin, open the dialog
		setDialogOpen(true);
	};

	return (
		<div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-6 overflow-visible pt-20">
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.1, ease: "easeInOut" }}
				className="col-span-1 md:col-span-2 h-full"
			>
				<Card className="relative overflow-hidden border-0 bg-card p-0 items-center flex shadow-sm h-full">
					<div className="absolute inset-0 border">
						<Image
							src="/static/images/hero3.webp"
							alt={t("home.hero.image_alt")}
							className="h-full w-full object-cover opacity-80"
						/>
						<div className="absolute inset-0" />
					</div>

					<div className="relative p-8 md:p-10 lg:p-12 flex flex-col gap-4">
						<motion.div
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ delay: 0.3, duration: 0.5 }}
						>
							<Badge className="text-sm bg-muted text-muted-foreground flex items-center gap-2 w-fit">
								<span>🍕</span>
								<span className="text-primary">{t("home.hero.badge")}</span>
							</Badge>
						</motion.div>
						<motion.h1
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.4, duration: 0.5 }}
							className="font-bold text-3xl md:text-4xl"
						>
							{t("home.hero.welcome", {
								name: session?.user?.name || t("home.hero.guest"),
							})}
						</motion.h1>
						<motion.p
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.5, duration: 0.5 }}
							className="text-muted-foreground text-sm md:text-base max-w-xl"
						>
							<span className="text-primary font-semibold">
								{t("home.hero.delivery.highlight")}
							</span>{" "}
							{t("home.hero.delivery.text")}
						</motion.p>
					</div>
				</Card>
			</motion.div>

			<motion.div
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6, ease: "easeInOut" }}
				className="col-span-1 h-full"
			>
				<Card className="relative overflow-visible border bg-linear-to-br from-card via-card to-accent/30 p-6 pt-28 flex flex-col justify-between group cursor-pointer hover:shadow-xl transition-all duration-300 shadow-lg mt-0 h-full">
					<div className="absolute -top-20 left-1/2 -translate-x-1/2 flex justify-center pointer-events-none z-20">
						<motion.div
							animate={{ y: [0, -10, 0] }}
							transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
							className="size-44 rounded-full bg-background shadow-xl flex items-center justify-center p-1.5 ring-1 ring-border/50 border"
						>
							<Image
								src={itemImage}
								alt={itemName}
								className="size-full p-2 rounded-full object-cover"
							/>
						</motion.div>
					</div>

					<div className="relative z-10 flex flex-col items-center text-center h-full gap-4">
						<div className="space-y-3 w-full">
							<div>
								<h2 className="text-2xl font-bold text-foreground tracking-tight transition-colors">
									{t("home.hero.kiddy_box.title", { name: itemName })}
								</h2>
								<div className="flex items-center justify-center gap-1.5 text-xs font-medium text-muted-foreground mt-1"></div>
							</div>

							<p className="text-sm text-muted-foreground leading-relaxed">
								{itemDescription}
							</p>
						</div>

						<div className="mt-auto w-full">
							<button
								type="button"
								onClick={handleCtaClick}
								className="w-full py-2.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 group/btn cursor-pointer"
							>
								<Utensils className="w-4 h-4 transition-transform" />
								{t("home.hero.kiddy_box.add_to_cart")}
							</button>
						</div>
					</div>
				</Card>
			</motion.div>

			{item && (
				<ItemDialog
					item={item}
					sizes={itemSizes}
					open={dialogOpen}
					onOpenChange={setDialogOpen}
				/>
			)}
		</div>
	);
}
